// @ts-check

/**
 * @file Renders a single user-role list row.
 */

import {Link} from "expo-router"
import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import {memo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {Pressable, Text, View} from "react-native-propforge"
import {TRANSLATIONS, translateRole} from "./shared"

/** @import {HrefObject} from "expo-router" */
/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} UserRoleRowProps
 * @property {string | HrefObject} editPath Route target for editing the role.
 * @property {"en" | "da" | "de"} locale Locale used for row labels.
 * @property {(userRoleId: string | number) => unknown} onDeletePress Delete callback.
 * @property {string} role Backend role identifier.
 * @property {string | number} userRoleId User-role identifier.
 */

/** @typedef {Record<string, never>} UserRoleRowState */

/**
 * User role list row.
 * @augments {ShapeComponent<UserRoleRowProps, UserRoleRowState>}
 */
class UserRoleRow extends ShapeComponent {
  /**
   * Renders the role label and row actions.
   * @returns {ReactNode} User-role row content.
   */
  render() {
    const {editPath, locale, role, userRoleId} = this.p
    const translation = TRANSLATIONS[locale]
    return (
      <View
        style={styles.row ||= {
          alignItems: "center",
          backgroundColor: "#ffffff",
          borderColor: "#d1d5db",
          borderRadius: 12,
          borderWidth: 1,
          flexDirection: "row",
          gap: 12,
          justifyContent: "space-between",
          marginBottom: 12,
          padding: 16
        }}
        testID={"userRoleRow-" + userRoleId}
      >
        <Text
          style={styles.rowText ||= {
            color: "#111827",
            flex: 1,
            fontSize: 16,
            fontWeight: 600
          }}
          testID={"userRoleRow-" + userRoleId + "/role"}
        >
          {translateRole(locale, role)}
        </Text>
        <View
          style={styles.actions ||= {
            flexDirection: "row",
            gap: 8
          }}
          testID={"userRoleRow-" + userRoleId + "/actions"}
        >
          <Link
            asChild
            href={editPath}
          >
            <Pressable
              style={styles.editButton ||= {
                alignItems: "center",
                backgroundColor: "#dbeafe",
                borderRadius: 8,
                flexDirection: "row",
                paddingHorizontal: 12,
                paddingVertical: 10
              }}
              testID={"userRoleRow-" + userRoleId + "/editButton"}
            >
              <View
                style={styles.editButtonIcon ||= {
                  marginRight: 8,
                  width: 14
                }}
                testID={"userRoleRow-" + userRoleId + "/editButton/icon"}
              >
                <FontAwesome6 color="#1d4ed8" name="pen-to-square" size={14} />
              </View>
              <Text
                style={styles.editButtonText ||= {
                  color: "#1d4ed8",
                  fontSize: 14,
                  fontWeight: 600
                }}
                testID={"userRoleRow-" + userRoleId + "/editButton/text"}
              >
                {translation.edit}
              </Text>
            </Pressable>
          </Link>
          <Pressable
            onPress={this.tt.onDeleteButtonPress}
            style={styles.deleteButton ||= {
              alignItems: "center",
              backgroundColor: "#fee2e2",
              borderRadius: 8,
              flexDirection: "row",
              paddingHorizontal: 12,
              paddingVertical: 10
            }}
            testID={"userRoleRow-" + userRoleId + "/deleteButton"}
          >
            <View
              style={styles.deleteButtonIcon ||= {
                marginRight: 8,
                width: 14
              }}
              testID={"userRoleRow-" + userRoleId + "/deleteButton/icon"}
            >
              <FontAwesome6 color="#b91c1c" name="trash" size={14} />
            </View>
            <Text
              style={styles.deleteButtonText ||= {
                color: "#b91c1c",
                fontSize: 14,
                fontWeight: 600
              }}
              testID={"userRoleRow-" + userRoleId + "/deleteButton/text"}
            >
              {translation.delete}
            </Text>
          </Pressable>
        </View>
      </View>
    )
  }

  /**
   * Forwards delete presses with this row's user-role id.
   * @returns {void} No return value.
   */
  onDeleteButtonPress = () => {
    this.p.onDeletePress(this.p.userRoleId)
  }
}

/** @type {NamedExoticComponent<UserRoleRowProps>} */
const UserRoleRowComponent = memo(shapeComponent(UserRoleRow))

export default UserRoleRowComponent
